import { Divider, ListItemText } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import type { PlanOverview } from '@vy/api-definition';
import { getSelectorAttribute, plansOverviewPageSelectors } from '@vy/app-selectors';
import { PLAN_STATUSES } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDeletePlan } from '../../../../../hooks/requests/use-delete-plan';
import { useUpdatePlan } from '../../../../../hooks/requests/use-update-plan';
import { useMenu } from '../../../../../hooks/use-menu';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';
import { runMutation } from '../../../../../utils/vy-mutation';
import type { NewPlanAssignment } from '../../plans-page-types/new-plan-assignment-schema';
import type { PlanAssignmentInput } from '../../plans-page-types/plan-assignment-input-schema';
import { DeletePlanDialog } from '../DeletePlanDialog';
import { DuplicateOptionsDialog } from '../../../CreatePlanPage/create-plan-page-components/DuplicateOptionsDialog/DuplicateOptionsDialog';
import { useDialog } from '../../../../../hooks/use-dialog';
import { CreateContractDialog } from '../../../GetStartedPlansPage/components/CardsSections/components/CreateContractDialog';
import { PLAN_CREATION_REASONS } from '../../../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import { DuplicatePlanSavingOption } from '../../../CreatePlanPage/create-plan-page-components/DuplicateOptionsDialog/types/duplicate-plan-saving-option';

type PlanDotMenuModalProps = {
  children?: React.ReactElement;
  refetchFunction: () => void;
  plan: PlanOverview;
  apiGridRef?: MutableRefObject<GridApiCommunity>;
  rowId?: string;
};

export const PlanMenuModal: React.FC<PlanDotMenuModalProps> = ({
  children,
  plan,
  refetchFunction,
  apiGridRef,
  rowId,
}) => {
  const navigate = useVyNavigate();
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();

  const {
    isDialogOpen: isSavingOptionsDialogOpen,
    handleOpen: handleSavingOptionsDialogOpen,
    handleClose: handleSavingOptionsDialogClose,
  } = useDialog();

  const {
    isDialogOpen: isCreateContractDialogOpen,
    handleOpen: handleCreateContractDialogOpen,
    handleClose: handleCreateContractDialogClose,
  } = useDialog();

  const { setValue } = useFormContext<PlanAssignmentInput, NewPlanAssignment>();

  const updatePlanMutation = useUpdatePlan();

  const deletePlanMutation = useDeletePlan();

  useEffect(() => {
    if (isNullOrUndefined(apiGridRef) || isNullOrUndefined(rowId)) {
      return;
    }

    apiGridRef.current?.selectRow(rowId, isOpen);
  }, [apiGridRef, isOpen, rowId]);

  const handleUpdateContract = () => {
    handleMenuClose();
    setValue('contract.planId', plan.id);
  };

  const handleClonePlan = () => {
    handleSavingOptionsDialogOpen();
  };

  const handleSelectDuplicatePlanSavingOption = (duplicatePlanSavingOption: DuplicatePlanSavingOption) => {
    if (duplicatePlanSavingOption === PLAN_CREATION_REASONS.NewContract) {
      handleSavingOptionsDialogClose();
      handleCreateContractDialogOpen();

      return;
    }

    navigate(`/plans/create?cloneFromPlanId=${plan.id}`);
  };

  const onContractDialogClose = () => {
    handleCreateContractDialogClose();
    handleSavingOptionsDialogOpen();
  };

  const handleUpdatePlan = () => {
    navigate(`/plans/${plan.id}/update`);
  };

  const handleDeletePlan = async () => {
    handleMenuClose();
    await runMutation(
      deletePlanMutation,
      { planId: plan.id },
      {
        pending: 'Deleting plan...',
        success: 'Plan deleted',
        error: 'Failed to delete plan',
      },
      {
        onSuccess: async () => refetchFunction(),
      },
    );
  };

  const handlePlanStatusChange = useCallback(async () => {
    const status = plan.status === PLAN_STATUSES.Active ? PLAN_STATUSES.Inactive : PLAN_STATUSES.Active;

    handleMenuClose();
    await runMutation(
      updatePlanMutation,
      { planId: plan.id, updatePlanRequest: { status } },
      {
        pending: 'Updating plan...',
        success: 'Plan updated',
        error: 'Failed to update plan',
      },
      {
        onSuccess: async () => refetchFunction(),
      },
    );
  }, [handleMenuClose, plan.id, plan.status, refetchFunction, updatePlanMutation]);

  const isActivated = plan.status === PLAN_STATUSES.Active;

  return (
    <>
      {isNotNullOrUndefined(children) &&
        React.cloneElement(children, {
          onClick: handleMenuOpen,
        })}

      <Menu
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            className: 'max-w-[200px] w-full rounded-lg',
          },
        }}
      >
        <MenuItem onClick={handleUpdateContract}>
          <ListItemText primary="Assign customer" />
        </MenuItem>

        <MenuItem onClick={handlePlanStatusChange}>
          <ListItemText primary={isActivated ? 'Deactivate' : 'Activate'} />
        </MenuItem>

        <MenuItem onClick={handleUpdatePlan}>
          <ListItemText primary="Update plan" />
        </MenuItem>

        <MenuItem onClick={handleClonePlan} {...getSelectorAttribute(plansOverviewPageSelectors.duplicateButton)}>
          <ListItemText primary="Duplicate plan" />
        </MenuItem>

        <Divider />
        <DeletePlanDialog
          disabled={doesPlanHaveContracts(plan)}
          planName={plan.name}
          onSubmit={handleDeletePlan}
        />
      </Menu>

      <DuplicateOptionsDialog
        isOpen={isSavingOptionsDialogOpen}
        onClose={handleSavingOptionsDialogClose}
        onSubmit={handleSelectDuplicatePlanSavingOption}
      />
      <CreateContractDialog
        isOpen={isCreateContractDialogOpen}
        onClose={onContractDialogClose}
        cloneFromPlanId={plan.id}
      />
    </>
  );
};

function doesPlanHaveContracts(plan: PlanOverview) {
  return plan.customersCount > 0;
}
